import React, { useRef, useState } from 'react';
import { ImagePlus, Music, RefreshCw, X } from 'lucide-react';
import { uploadPublicMedia } from '../../api/media';
import { useI18n } from '../../i18n/I18nContext';
import './MediaSlot.css';

// What the backend's public upload takes (POST /media/public-image|audio).
const RULES = {
  image: { accept: 'image/png,image/jpeg,image/webp,image/gif', types: /^image\/(png|jpeg|webp|gif)$/, maxMb: 5 },
  audio: { accept: 'audio/mpeg,audio/mp4,audio/x-m4a,audio/ogg,audio/wav,audio/x-wav,audio/webm', types: /^audio\/(mpeg|mp3|mp4|x-m4a|ogg|wav|x-wav|webm)$/, maxMb: 10 },
};

/**
 * MediaSlot — a picture or a sound on a question card or a word: pick a file or drop it on the
 * slot, watch it upload, see or hear what is attached, replace or remove it. Controlled by
 * `value` (the address, or '') and `onChange(address | null)`. `removable={false}` where the API
 * has no way to take a file off (a word's picture).
 *
 *   <MediaSlot kind="image" value={q.imageUrl} onChange={(url) => setQ({ ...q, imageUrl: url })} />
 */
function MediaSlot({ kind = 'image', value, onChange, disabled = false, removable = true, className = '' }) {
  const { t } = useI18n();
  const rule = RULES[kind];
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null); // null when idle, 0-100 while uploading
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const busy = progress !== null;

  const upload = async (file) => {
    if (!file || busy || disabled) return;
    setError(null);
    if (!rule.types.test(file.type)) {
      setError(t(kind === 'image' ? 'mediaSlot.wrongImageType' : 'mediaSlot.wrongAudioType'));
      return;
    }
    if (file.size > rule.maxMb * 1024 * 1024) {
      setError(t('mediaSlot.tooBig', { mb: rule.maxMb }));
      return;
    }
    setProgress(0);
    try {
      const res = await uploadPublicMedia(kind, file, setProgress);
      if (!res?.url) throw new Error('no url');
      onChange(res.url);
    } catch (err) {
      setError(t(err?.code === 403 ? 'mediaSlot.forbidden' : 'mediaSlot.failed'));
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    upload(e.dataTransfer?.files?.[0]);
  };

  const pick = () => inputRef.current?.click();

  return (
    <div
      className={`media-slot media-slot--${kind}${dragging ? ' is-dragging' : ''}${value ? ' has-value' : ''} ${className}`.trim()}
      onDragOver={(e) => { e.preventDefault(); if (!dragging && !disabled) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      {value && !busy && (
        kind === 'image'
          ? <img className="media-slot__img" src={value} alt={t('mediaSlot.imageAlt')} />
          // eslint-disable-next-line jsx-a11y/media-has-caption
          : <audio className="media-slot__audio" src={value} controls preload="none" />
      )}

      {busy ? (
        <span className="media-slot__progress" role="status">
          <span className="media-slot__bar"><span style={{ width: `${progress}%` }} /></span>
          {t('mediaSlot.uploading', { percent: progress })}
        </span>
      ) : value ? (
        <span className="media-slot__actions">
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={pick} disabled={disabled}>
            <RefreshCw aria-hidden="true" />
            {t('mediaSlot.replace')}
          </button>
          {removable && (
            <button
              type="button"
              className="ui-icon-btn"
              onClick={() => onChange(null)}
              disabled={disabled}
              title={t('mediaSlot.remove')}
              aria-label={t('mediaSlot.remove')}
            >
              <X aria-hidden="true" />
            </button>
          )}
        </span>
      ) : (
        <span className="media-slot__empty">
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={pick} disabled={disabled}>
            {kind === 'image' ? <ImagePlus aria-hidden="true" /> : <Music aria-hidden="true" />}
            {t(kind === 'image' ? 'mediaSlot.addImage' : 'mediaSlot.addAudio')}
          </button>
          <span className="media-slot__hint">{t('mediaSlot.dropHint')}</span>
        </span>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={rule.accept}
        hidden
        data-no-style
        onChange={(e) => upload(e.target.files?.[0])}
      />
      {error && <p className="ui-error media-slot__error" role="alert">{error}</p>}
    </div>
  );
}

export default MediaSlot;
